import { type Asset } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/i18n/language-hooks';
import type { FinancialInfo, RevenueStream } from '@/services/goalPlanningStorage';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const revenueStreamSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  amount: z.coerce.number().min(0, 'Amount must be positive'),
  frequency: z.enum(['monthly', 'yearly']),
});

const formSchema = z.object({
  salary: z.coerce.number().min(0, 'Salary must be positive'),
  currency: z.string().min(1, 'Currency is required'),
  revenueStreams: z.array(revenueStreamSchema).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface FinancialInfoStepProps {
  initialData?: FinancialInfo | null;
  assets: Asset[];
  isLoadingAssets: boolean;
  onSubmit: (data: FinancialInfo) => void;
}

export const FinancialInfoStep = ({
  initialData,
  assets,
  isLoadingAssets,
  onSubmit,
}: FinancialInfoStepProps) => {
  const { t } = useLanguage();
  const [revenueStreams, setRevenueStreams] = useState<RevenueStream[]>(
    initialData?.revenueStreams || []
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      salary: initialData?.salary || 0,
      currency: initialData?.currency || assets[0]?.code || 'USD',
      revenueStreams: initialData?.revenueStreams || [],
    },
  });

  const handleSubmit = (data: FormValues) => {
    // Calculate total income
    const monthlyRevenue = revenueStreams
      .filter((rs) => rs.frequency === 'monthly')
      .reduce((sum, rs) => sum + rs.amount, 0);
    const yearlyRevenue = revenueStreams
      .filter((rs) => rs.frequency === 'yearly')
      .reduce((sum, rs) => sum + rs.amount, 0);

    const monthlySalary = data.salary / 12; // Assuming salary is yearly
    const totalMonthlyIncome = monthlySalary + monthlyRevenue + yearlyRevenue / 12;
    const totalYearlyIncome = data.salary + monthlyRevenue * 12 + yearlyRevenue;

    const financialInfo: FinancialInfo = {
      salary: data.salary,
      currency: data.currency,
      revenueStreams,
      totalMonthlyIncome,
      totalYearlyIncome,
    };

    onSubmit(financialInfo);
  };

  const addRevenueStream = () => {
    const newStream: RevenueStream = {
      id: Date.now().toString(),
      name: '',
      amount: 0,
      frequency: 'monthly',
      currency: form.watch('currency'),
    };
    setRevenueStreams([...revenueStreams, newStream]);
  };

  const removeRevenueStream = (id: string) => {
    setRevenueStreams(revenueStreams.filter((rs) => rs.id !== id));
  };

  const updateRevenueStream = (id: string, updates: Partial<RevenueStream>) => {
    setRevenueStreams(revenueStreams.map((rs) => (rs.id === id ? { ...rs, ...updates } : rs)));
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6'>
        <div className='space-y-4'>
          <FormField
            control={form.control}
            name='salary'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('goalPlanning.financialInfo.salary')}</FormLabel>
                <FormControl>
                  <Input
                    type='number'
                    step='0.01'
                    placeholder={t('goalPlanning.financialInfo.salaryPlaceholder')}
                    {...field}
                  />
                </FormControl>
                <p className='text-muted-foreground text-xs'>
                  {t('goalPlanning.financialInfo.salaryHint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='currency'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('goalPlanning.financialInfo.currency')}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  disabled={isLoadingAssets}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isLoadingAssets
                            ? t('common.loading')
                            : t('goalPlanning.financialInfo.currencyPlaceholder')
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {assets.map((asset) => (
                      <SelectItem key={asset.id} value={asset.code}>
                        {asset.code} - {asset.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <FormLabel>{t('goalPlanning.financialInfo.revenueStreams')}</FormLabel>
              <Button type='button' variant='outline' size='sm' onClick={addRevenueStream}>
                <Plus className='mr-1 h-4 w-4' />
                {t('goalPlanning.financialInfo.addRevenueStream')}
              </Button>
            </div>

            {revenueStreams.length === 0 && (
              <p className='text-muted-foreground text-sm'>
                {t('goalPlanning.financialInfo.noRevenueStreams')}
              </p>
            )}

            {revenueStreams.map((stream) => (
              <div key={stream.id} className='flex gap-2 space-y-2 rounded-lg border p-3'>
                <Input
                  placeholder={t('goalPlanning.financialInfo.revenueStreamName')}
                  value={stream.name}
                  onChange={(e) => updateRevenueStream(stream.id, { name: e.target.value })}
                  className='flex-1'
                />
                <Input
                  type='number'
                  step='0.01'
                  placeholder={t('goalPlanning.financialInfo.revenueStreamAmount')}
                  value={stream.amount || ''}
                  onChange={(e) =>
                    updateRevenueStream(stream.id, {
                      amount: parseFloat(e.target.value) || 0,
                    })
                  }
                  className='w-32'
                />
                <Select
                  value={stream.frequency}
                  onValueChange={(value: 'monthly' | 'yearly') =>
                    updateRevenueStream(stream.id, { frequency: value })
                  }
                >
                  <SelectTrigger className='w-32'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='monthly'>
                      {t('goalPlanning.financialInfo.monthly')}
                    </SelectItem>
                    <SelectItem value='yearly'>{t('goalPlanning.financialInfo.yearly')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  onClick={() => removeRevenueStream(stream.id)}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <Button type='submit' className='w-full'>
          {t('goalPlanning.financialInfo.continue')}
        </Button>
      </form>
    </Form>
  );
};
